// AppShell — patrón global (§6.1 de la arquitectura): sidebar armado por rol + topbar + contenido.
// Port fiel del design system del mock (surekeep/mockups/_shared/layout.css): sidebar 224px
// (bg card) colapsable, header 48px con marca navy, nav con grupos en uppercase e item activo en
// `accent`, footer con usuario/acciones y topbar 48px con título. Cada dominio define su menú en
// `domains/<x>/nav.ts` (AppShellNavGroup[]) y lo monta desde su ruta de portal.
import { useState, type ReactNode } from 'react';
import { Link, useRouterState, type LinkOptions } from '@tanstack/react-router';
import { ChevronLeft, LogOut, RefreshCw, Shield, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/hooks/useAuth';
import { hasMultiplePortals, portalForPathname } from '@/app/entry/portalAccess';
import { useScreenTitleStore } from './screenTitle';
import { useScreenControlsStore } from './screenControls';
import { useCompactViewport } from './useCompactViewport';
import { visibleNavGroups } from './navAccess';
import { resolveSessionIdentity } from './sessionIdentity';
import { useLogout } from './useLogout';

/**
 * Estado de conexión de la pantalla. Lo único que el sidebar dibuja es `stub` — el default si no se
 * declara —, que atenúa el item; `connected`, `partial` y `mock` son metadato y se pintan igual.
 */
export type NavItemStatus = 'connected' | 'partial' | 'mock' | 'stub';

export interface AppShellNavItem {
    /** Slug de la pantalla — lookup del título en las rutas `$section`. El Home no lleva. */
    slug?: string;
    label: string;
    icon: LucideIcon;
    /** Chip a la derecha del label (p.ej. "Pronto"). */
    badge?: string;
    /** Destino type-safe — crear con `linkOptions()` en el nav.ts del dominio. */
    link: LinkOptions;
    /** Activo solo en match exacto (Home del portal). */
    exact?: boolean;
    /** Título largo para el topbar (default: `label`). P.ej. "Configuración global · parámetros…". */
    pageTitle?: string;
    /** Crumb del topbar cuando el mock de la pantalla lo alarga (default: el nombre del portal). */
    crumb?: string;
    /** Estado de conexión (default: 'stub' = en construcción). Se actualiza al conectar. */
    status?: NavItemStatus;
    /**
     * Permiso o permisos RBAC requeridos para VER el item; con varios se piden TODOS. Sin
     * `permission` = siempre visible.
     */
    permission?: string | readonly string[];
}

export interface AppShellNavGroup {
    /** Encabezado del grupo; sin label = bloque superior (Home). */
    label?: string;
    items: AppShellNavItem[];
}

/** Busca un item por slug en el nav de un dominio (título de las rutas `$section`). */
export function findNavItem(nav: AppShellNavGroup[], slug: string): AppShellNavItem | undefined {
    for (const group of nav) {
        const hit = group.items.find((item) => item.slug === slug);
        if (hit) return hit;
    }
    return undefined;
}

/**
 * El aire que el shell le pone a TODA pantalla. Vive acá porque es el contrato con las pantallas:
 * quien necesite ir a sangre completa tiene que cancelar EXACTAMENTE esto, y si un día cambia, debe
 * cambiar en un solo lugar.
 */
export const SHELL_CONTENT_PADDING = 'px-5 py-4';

/**
 * Lo que cancela ese aire — el negativo exacto de `SHELL_CONTENT_PADDING`.
 *
 * No lo uses suelto: usa `<ScreenBand>`, que ya lo aplica. Se exporta para el caso raro de una
 * pantalla que necesite sangrar algo que no es una banda superior.
 */
export const SHELL_CONTENT_BLEED = '-mx-5 -mt-4';

/**
 * El negativo de los CUATRO lados, con el alto que repone lo que el sangrado vertical le quita al
 * contenedor. `fill` para la pantalla que no scrollea; `grow` para la que sí. Úsalo vía
 * `<ScreenFrame>`, que ya lo aplica.
 */
export const SHELL_CONTENT_FULL_BLEED = {
    fill: '-mx-5 -my-4 h-[calc(100%+2rem)]',
    grow: '-mx-5 -my-4 min-h-[calc(100%+2rem)]',
} as const;

/** Pathname que produce el link de un item (resuelve `$params`), para saber cuál está activo. */
function itemPathname(item: AppShellNavItem): string {
    const { to, params } = item.link as { to?: string; params?: Record<string, string> };
    if (!to) return '';
    return to.replace(/\$([A-Za-z0-9_]+)/g, (_match, key: string) => params?.[key] ?? '');
}

// .sidebar-item del mock: px-2 py-[5px], radius 6px. El Link concatena estas clases con las de su
// estado, así que la base no lleva color, peso ni fondo.
const NAV_ITEM =
    'flex w-full items-center gap-2 rounded-md px-2 py-[5px] text-[12.5px] transition-colors';
/** Hover tenue solo en los inactivos, para que no se confunda con el activo. */
const NAV_ITEM_INACTIVE = 'font-medium text-foreground/80 hover:bg-accent/50 hover:text-foreground';
/** El activo: fondo accent, negrita y barra izquierda. */
const NAV_ITEM_ACTIVE =
    'bg-accent font-semibold text-foreground shadow-[inset_2px_0_0_hsl(var(--primary))]';

export function AppShell({
    title,
    nav,
    actions,
    children,
}: {
    /** Nombre del portal — crumb del topbar (p.ej. "Portal Super Admin"). */
    title: string;
    nav: AppShellNavGroup[];
    /**
     * Acciones del topbar, a la izquierda del bloque de usuario (mock: buscador rápido +
     * "Nueva venta" del Portal Vendedor). Cada portal decide las suyas; el gateo por permiso
     * es responsabilidad del contenido que se pasa acá, no del shell.
     */
    actions?: ReactNode;
    children: ReactNode;
}) {
    const user = useAuthStore((s) => s.user);
    const handleLogout = useLogout();
    // El sidebar sigue al ancho; lo que el usuario elige a mano vale mientras no cruce el corte,
    // y por eso la elección viaja etiquetada con el ancho en el que se tomó.
    const isCompact = useCompactViewport();
    const [manualChoice, setManualChoice] = useState<{
        compact: boolean;
        collapsed: boolean;
    } | null>(null);
    const collapsed = manualChoice?.compact === isCompact ? manualChoice.collapsed : isCompact;
    const setCollapsed = (next: boolean): void => {
        setManualChoice({ compact: isCompact, collapsed: next });
    };
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    // Menú RBAC-driven: esconde/muestra por PERMISO (patrón Fase 2). Item sin `permission` = visible;
    // grupo que queda sin items visibles = se oculta también.
    const { hasPermission, permissions, roleAssignments } = useAuth();
    const visibleNav = visibleNavGroups(nav, hasPermission);
    // Título de pantalla del topbar = item activo del nav (crumb = nombre del portal). La pantalla
    // puede pisarlo cuando su mock lo nombra con un dato del silo («Mi tienda · Iztapalapa»).
    const activeItem = visibleNav.flatMap((g) => g.items).find((i) => itemPathname(i) === pathname);
    const titleOverride = useScreenTitleStore((s) => s.title);
    // Hueco para los controles de la pantalla, que se monta desde el `Outlet` con `<ScreenControls>`.
    const setControlsSlot = useScreenControlsStore((s) => s.setSlot);
    const screenTitle = titleOverride ?? activeItem?.pageTitle ?? activeItem?.label;
    const crumb = activeItem?.crumb ?? title;

    // Rol y tienda del pie y del topbar: los de la asignación con que opera el portal actual.
    const { roleName, scopeLabel } = resolveSessionIdentity(
        roleAssignments,
        portalForPathname(pathname)?.operatingScope,
    );
    const personName = user?.displayName ?? 'Sesión activa';
    const roleAndStore = [roleName, scopeLabel].filter(Boolean).join(' · ');

    return (
        <div className="flex h-screen overflow-hidden bg-background text-foreground">
            {/* Sidebar — .sidebar (224px; colapsado = riel de íconos) */}
            <aside
                className={cn(
                    'flex flex-none flex-col border-r border-border bg-card transition-[width]',
                    collapsed ? 'w-14' : 'w-56',
                )}
            >
                {/* .sidebar-header (48px): marca + botón de colapso */}
                <div
                    className={cn(
                        'flex h-12 items-center gap-2 border-b border-border',
                        collapsed ? 'justify-center px-0' : 'px-4',
                    )}
                >
                    <span className="flex size-7 flex-none items-center justify-center rounded-md bg-primary text-primary-foreground">
                        <Shield className="size-3.5" strokeWidth={2.5} aria-hidden />
                    </span>
                    {!collapsed && (
                        <span className="flex-1 truncate text-[13px] font-semibold tracking-tight">
                            SureKeep
                        </span>
                    )}
                    {!collapsed && (
                        <button
                            type="button"
                            onClick={() => {
                                setCollapsed(true);
                            }}
                            aria-label="Colapsar menú"
                            className="flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                        >
                            <ChevronLeft className="size-3.5" aria-hidden />
                        </button>
                    )}
                </div>

                {/* .sidebar-nav: grupos + items */}
                <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2">
                    {collapsed && (
                        <button
                            type="button"
                            onClick={() => {
                                setCollapsed(false);
                            }}
                            aria-label="Expandir menú"
                            className={cn(NAV_ITEM, NAV_ITEM_INACTIVE, 'mb-1 justify-center')}
                        >
                            <ChevronLeft className="size-3.5 rotate-180" aria-hidden />
                        </button>
                    )}
                    {visibleNav.map((group, i) => (
                        <div key={group.label ?? `group-${String(i)}`}>
                            {group.label && !collapsed && (
                                <div className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                    {group.label}
                                </div>
                            )}
                            {group.label && collapsed && (
                                <div className="mx-2 my-2 border-t border-border" aria-hidden />
                            )}
                            {group.items.map((item) => {
                                const Icon = item.icon;
                                // Stub = "en construcción": item atenuado (recupera al hover).
                                const isStub = (item.status ?? 'stub') === 'stub';
                                let title: string | undefined;
                                if (collapsed) title = item.label;
                                else if (isStub) title = 'En construcción';
                                return (
                                    <Link
                                        key={item.slug ?? item.label}
                                        {...item.link}
                                        activeOptions={{ exact: item.exact ?? false }}
                                        title={title}
                                        className={cn(
                                            NAV_ITEM,
                                            collapsed && 'justify-center',
                                            isStub &&
                                                'opacity-55 transition-opacity hover:opacity-100',
                                        )}
                                        activeProps={{ className: NAV_ITEM_ACTIVE }}
                                        inactiveProps={{ className: NAV_ITEM_INACTIVE }}
                                    >
                                        <Icon className="size-3.5 shrink-0" aria-hidden />
                                        {/* Colapsado el nombre no se dibuja pero sigue leyéndose:
                                            el `title` no existe en una pantalla táctil. */}
                                        <span className={cn('truncate', collapsed && 'sr-only')}>
                                            {item.label}
                                        </span>
                                        {!collapsed && item.badge && (
                                            <span className="ml-auto flex-none rounded bg-muted px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                {item.badge}
                                            </span>
                                        )}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* .sidebar-footer: usuario + salir */}
                <div className="border-t border-border p-2">
                    {!collapsed && (
                        <div className="px-2 pb-2 pt-1 text-[11px] text-muted-foreground">
                            <span className="block truncate text-[12px] font-semibold text-foreground">
                                {personName}
                            </span>
                            <span className="block truncate">{roleAndStore}</span>
                        </div>
                    )}
                    {hasMultiplePortals(permissions) && (
                        <Link
                            to="/select-role"
                            title={collapsed ? 'Cambiar de rol' : undefined}
                            className={cn(
                                NAV_ITEM,
                                NAV_ITEM_INACTIVE,
                                collapsed && 'justify-center',
                            )}
                        >
                            <RefreshCw className="size-3.5 shrink-0" aria-hidden />
                            {!collapsed && 'Cambiar de rol'}
                        </Link>
                    )}
                    <button
                        type="button"
                        onClick={() => void handleLogout()}
                        title={collapsed ? 'Cerrar sesión' : undefined}
                        className={cn(NAV_ITEM, NAV_ITEM_INACTIVE, collapsed && 'justify-center')}
                    >
                        <LogOut className="size-3.5 shrink-0" aria-hidden />
                        {!collapsed && 'Cerrar sesión'}
                    </button>
                </div>
            </aside>

            {/* Main — .main: topbar (48px: crumb + título de pantalla + usuario) + .content */}
            <main className="flex flex-1 flex-col overflow-hidden">
                <header className="flex h-12 flex-none items-center gap-4 border-b border-border bg-card px-5">
                    {screenTitle ? (
                        // Piso de ancho: con `min-w-0` el título se encogía a 0 px y el vendedor
                        // dejaba de ver en qué pantalla está.
                        <div className="min-w-24 leading-tight">
                            <div className="truncate text-[10px] text-muted-foreground">
                                {crumb}
                            </div>
                            {/* `h1` y no `div`: el título de pantalla es EL encabezado de la página.
                                Sin él, la lista de encabezados de un lector arranca en nivel 2 y sin
                                nombre — no hay forma de saber dónde estás. Afecta a las 4 portales. */}
                            <h1 className="truncate text-[13px] font-semibold">{screenTitle}</h1>
                        </div>
                    ) : (
                        <h1 className="truncate text-[13px] font-semibold">{title}</h1>
                    )}
                    {/* Los controles de la pantalla van pegados al título: ahí el mock pone el
                        alcance sobre el que se mira. `min-w-0` para que trunquen y no empujen. */}
                    <div ref={setControlsSlot} className="flex min-w-0 items-center gap-2" />
                    <div className="flex-1" />
                    {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
                    <div className="flex min-w-0 max-w-48 flex-none items-center gap-2 border-l border-border pl-3">
                        <div className="min-w-0 text-right leading-tight">
                            <div className="truncate text-[12px] font-medium">
                                {roleName ?? personName}
                            </div>
                            <div className="truncate text-[10px] text-muted-foreground">
                                {scopeLabel ?? ''}
                            </div>
                        </div>
                    </div>
                </header>
                <div className={cn('flex-1 overflow-y-auto bg-background', SHELL_CONTENT_PADDING)}>
                    {children}
                </div>
            </main>
        </div>
    );
}
